/* "Corrected documents" banner: a completed invoice changed, a corrected slip rebuilt its documents —
   everyone who used the old copies acknowledges here (shown on every document panel). */
import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { AlertTriangle, CheckCircle2, ChevronDown, ChevronRight, Loader2 } from "lucide-react";
import { API, getAuthHeader } from "@/App";
import { Button } from "@/components/ui/button";

const when = (iso) => { try { return new Date(iso).toLocaleString(); } catch { return iso; } };

const AckRow = ({ a, mine, onDone }) => {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const ack = async () => {
    setBusy(true);
    try { await axios.post(`${API}/order-register/doc-acks/${a.id}/ack`, {}, { headers: getAuthHeader() }); toast.success(`${a.invoice_no} acknowledged`); onDone(); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not acknowledge"); }
    setBusy(false);
  };
  return (
    <div className="rounded-md border border-amber-200 bg-white px-2 py-1.5" data-testid={`doc-ack-${a.id}`}>
      <div className="flex flex-wrap items-center gap-2">
        <button type="button" onClick={() => setOpen(o => !o)} className="inline-flex items-center gap-1 font-mono font-semibold text-amber-900" data-testid={`doc-ack-toggle-${a.id}`}>
          {open ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}{a.invoice_no}</button>
        <span className="text-slate-600">Corrected {when(a.corrected_at)} by {a.corrected_by}</span>
        <span className="text-slate-500">· you used: {(a.kinds || []).join(", ") || "documents"}</span>
        {!mine && <span className="rounded bg-slate-100 px-1.5 text-slate-600">{a.user_name}</span>}
        {mine && <Button size="sm" className="ml-auto h-6 bg-amber-600 px-2 text-[11px] hover:bg-amber-700" disabled={busy} onClick={ack} data-testid={`doc-ack-btn-${a.id}`}>
          {busy ? <Loader2 className="mr-1 h-3 w-3 animate-spin" /> : <CheckCircle2 className="mr-1 h-3 w-3" />} I replaced my copies</Button>}
      </div>
      {open && <ul className="mt-1 list-disc pl-6 text-slate-700" data-testid={`doc-ack-changes-${a.id}`}>
        {(a.changes || []).length ? a.changes.map((c, i) => <li key={i}>{c}</li>) : <li>Changes recorded in the audit log</li>}</ul>}
    </div>
  );
};

export const DocAcksBanner = () => {
  const isAdmin = (() => { try { return JSON.parse(localStorage.getItem("user") || "{}").role === "admin"; } catch { return false; } })();
  const [mine, setMine] = useState([]);
  const [all, setAll] = useState([]);
  const [showAll, setShowAll] = useState(false);
  const load = useCallback(async () => {
    try {
      setMine((await axios.get(`${API}/order-register/doc-acks/mine`, { headers: getAuthHeader() })).data.rows);
      if (isAdmin) setAll((await axios.get(`${API}/order-register/doc-acks/mine`, { headers: getAuthHeader(), params: { everyone: true } })).data.rows);
    } catch { /* banner is optional */ }
  }, [isAdmin]);
  useEffect(() => { load(); const t = setInterval(load, 120000); return () => clearInterval(t); }, [load]);
  const others = all.filter(a => !mine.some(m => m.id === a.id));
  if (!mine.length && !others.length) return null;
  return (
    <div className="space-y-1.5 rounded-lg border border-amber-300 bg-amber-50 p-2 text-xs" data-testid="doc-acks-banner">
      <p className="flex items-center gap-1.5 font-semibold text-amber-900"><AlertTriangle className="h-4 w-4" />
        {mine.length ? `${mine.length} corrected invoice${mine.length > 1 ? "s" : ""} — replace the old copies you printed / downloaded` : "Team acknowledgements pending"}
        {isAdmin && others.length > 0 && <button type="button" onClick={() => setShowAll(s => !s)} className="ml-auto font-normal text-amber-800 underline" data-testid="doc-acks-all-toggle">
          {showAll ? "Hide" : "Show"} {others.length} pending from team</button>}</p>
      {mine.map(a => <AckRow key={a.id} a={a} mine onDone={load} />)}
      {showAll && others.map(a => <AckRow key={a.id} a={a} mine={false} onDone={load} />)}
    </div>
  );
};
